import { Check } from '@untitled-ui/icons-react'
import React from 'react'
import {
  Collection,
  composeRenderProps,
  Header,
  ListBox as AriaListBox,
  ListBoxItem as AriaListBoxItem,
  ListBoxItemProps,
  ListBoxProps as AriaListBoxProps,
  ListBoxSection,
  ListBoxSectionProps,
  Text
} from 'react-aria-components'
import { tv } from 'tailwind-variants'
import { composeTailwindRenderProps, listItemBaseStyles } from './utils'
import { t } from '@lingui/core/macro'
import { twMerge } from 'tailwind-merge'

type ListBoxProps<T> = Omit<AriaListBoxProps<T>, 'layout' | 'orientation'>

export function ListBox<T extends object>({
  children,
  ...props
}: ListBoxProps<T>) {
  return (
    <AriaListBox {...props} className={props.className}>
      {children}
    </AriaListBox>
  )
}

export function ListBoxItem(props: ListBoxItemProps) {
  const textValue =
    props.textValue ??
    (typeof props.children === 'string' ? props.children : undefined)
  return (
    <AriaListBoxItem
      {...props}
      textValue={textValue}
      className={composeRenderProps(props.className, (className, renderProps) =>
        listItemBaseStyles({ ...renderProps, className })
      )}>
      {composeRenderProps(props.children, children => (
        <>
          {children}
          <div className='absolute left-4 right-4 bottom-0 h-px bg-white/20 forced-colors:bg-[HighlightText] hidden [.group[data-selected]:has(+[data-selected])_&]:block' />
        </>
      ))}
    </AriaListBoxItem>
  )
}

export const dropdownItemStyles = tv({
  base: 'group w-full flex cursor-pointer select-none py-2.5 px-3.5 outline-0 justify-between',
  variants: {
    isDisabled: {
      false: '',
      true: 'text-neutral-300 cursor-default'
    },
    isFocused: {
      true: 'bg-neutral-50'
    },
    isHighlighted: {
      true: 'bg-highlight-50 text-highlight-500',
      false: ''
    },
    isSuggested: {
      true: 'bg-success-50',
      false: ''
    }
  },
  compoundVariants: [
    {
      isDisabled: true,
      isHighlighted: true,
      className: 'text-neutral-300'
    },
    {
      isDisabled: false,
      isHighlighted: true,
      className: 'text-highlight-500'
    },
    {
      isDisabled: false,
      isHighlighted: false,
      className: 'text-neutral-900'
    },
    {
      isDisabled: true,
      isHighlighted: false,
      className: 'text-neutral-300'
    },
    {
      isHighlighted: true,
      isSuggested: true,
      className: 'text-highlight-500'
    }
  ]
})

export interface DropdownItemProps extends ListBoxItemProps {
  leadingAdornment?: React.ReactNode
  label: string
  description?: string
  className?: string
  isHighlighted?: boolean
  trailingAdornment?: React.ReactNode
  isSuggested?: boolean
}

export function DropdownItem({
  leadingAdornment,
  trailingAdornment,
  label,
  isSuggested,
  className,
  description,
  ...props
}: DropdownItemProps) {
  const textValue = props.textValue ?? label
  return (
    <AriaListBoxItem
      {...props}
      textValue={textValue}
      className={composeTailwindRenderProps(
        params =>
          dropdownItemStyles({
            ...params,
            isHighlighted: props.isHighlighted,
            isSuggested
          }),
        twMerge('text-body card', className)
      )}>
      {composeRenderProps(props.children, children => (
        <div
          className={
            'flex group flex-row w-full items-center justify-between gap-3'
          }>
          {leadingAdornment && (
            <div className='flex shrink-0 justify-center items-center'>
              {leadingAdornment}
            </div>
          )}
          {children != null ? (
            <div className='min-w-0 grow'>{children}</div>
          ) : (
            <div
              className={
                'flex flex-col gap-0.5 w-full label-block items-start justify-start grow'
              }>
              {label && (
                <Text
                  slot='label'
                  className={`block text-base font-['Inter'] leading-tight label ${
                    !!description ? 'shrink-0' : 'truncate ...'
                  }`}>
                  {label}
                </Text>
              )}
              {description && (
                <Text
                  slot='description'
                  className="block text-xs font-medium font-['Plus Jakarta Sans'] leading-tight label-description truncate text-neutral-500 ...">
                  {description}
                </Text>
              )}
            </div>
          )}
          {trailingAdornment && (
            <div className='flex shrink-0 pl-4 group-selected:hidden'>
              {trailingAdornment}
            </div>
          )}
          {!trailingAdornment && isSuggested && (
            <div className='text-xs text-success-500 flex shrink-0 suggested-indicator group-selected:hidden'>
              {t`Suggested`}
            </div>
          )}
          <div className='flex shrink-0 justify-center items-center'>
            <Check
              width={18}
              className='text-highlight-600 hidden group-selected:inline-block'
            />
          </div>
        </div>
      ))}
    </AriaListBoxItem>
  )
}

export interface DropdownSectionProps<T> extends ListBoxSectionProps<T> {
  title?: string
}

export function DropdownSection<T extends object>(
  props: DropdownSectionProps<T>
) {
  return (
    <ListBoxSection className="first:-mt-[5px] after:content-[''] after:block after:h-[5px]">
      <Header className='text-sm font-semibold text-neutral-500 px-4 py-1 truncate sticky -top-[5px] -mt-px -mx-1 z-10 bg-neutral-100/60 backdrop-blur-md supports-[-moz-appearance:none]:bg-neutral-100 border-y [&+*]:mt-1'>
        {props.title}
      </Header>
      <Collection items={props.items}>{props.children}</Collection>
    </ListBoxSection>
  )
}
